import { useQuery } from "@tanstack/react-query";
import { createIdentityClient } from "@tcg/api-client";
import { History } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { DateJST } from "@/components/DateJST";
import { ErrorState } from "@/components/EmptyState";
import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { usePermissions } from "@/lib/auth/usePermissions";
import { useLiveInvalidate } from "@/realtime/hooks";

import { DASHBOARD_REFRESH_MS } from "./useTodoSources";

/** Entity types of the audit log named in plain words; anything else is "a record". */
const ENTITY_WORDS = new Set([
  "product",
  "variant",
  "collection",
  "order",
  "payment",
  "refund",
  "customer",
  "consignor",
  "consignment_item",
  "settlement",
  "payout",
  "unit",
  "inventory",
  "staff",
  "role",
  "tax_setting",
  "setting",
  "page",
  "theme",
  "discount",
]);

/** `catalog.product.updated` → `updated`; the verbs staff read are few. */
export function auditVerb(action: string): "created" | "updated" | "deleted" | "status" | "other" {
  const last = action.split(".").pop() ?? "";
  if (/^(created|added|received|issued)$/.test(last)) {
    return "created";
  }
  if (/^(deleted|removed|archived|voided|cancelled)$/.test(last)) {
    return "deleted";
  }
  if (/(status|published|unpublished|paid|shipped|confirmed)/.test(last)) {
    return "status";
  }
  if (/(updated|changed|edited|adjusted|patched)/.test(last)) {
    return "updated";
  }
  return "other";
}

export function auditEntity(entityType: string): string {
  const key = entityType.toLowerCase();
  return ENTITY_WORDS.has(key) ? key : "record";
}

/** "Who changed what" — the last 10 entries of the audit log (owner / manager: `audit.view`). */
export function RecentActivity() {
  const { t } = useTranslation("dashboard");
  const perms = usePermissions();
  const identity = useServiceClient(createIdentityClient);
  const allowed = perms.can("audit.view");
  // R21: every change anyone makes lands in the audit log (staff:audit) → refetch the last 10
  const poll = useLiveInvalidate("staff:audit", [["dashboard", "recent-activity"]], {
    pollMs: DASHBOARD_REFRESH_MS,
  });
  const query = useQuery({
    queryKey: ["dashboard", "recent-activity"],
    enabled: allowed,
    meta: { errorToast: false },
    refetchInterval: poll,
    refetchIntervalInBackground: false,
    queryFn: ({ signal }) =>
      unwrap(identity.GET("/v1/admin/audit-logs", { params: { query: { limit: 10 } }, signal })),
  });
  if (!allowed) {
    return null;
  }
  return (
    <section
      aria-labelledby="activity-title"
      data-slot="recent-activity"
      className="flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 id="activity-title" className="flex items-center gap-2 text-lg font-bold">
          <History className="size-5" aria-hidden />
          {t("activity.title")}
        </h2>
        <Link
          to="/settings/audit-log"
          className="min-h-touch inline-flex items-center text-sm underline underline-offset-4"
        >
          {t("activity.all")}
        </Link>
      </div>
      {query.isPending ? (
        <ul aria-hidden className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-10 animate-pulse rounded bg-muted" />
          ))}
        </ul>
      ) : query.isError ? (
        <ErrorState body={t("activity.error")} onRetry={() => void query.refetch()} />
      ) : query.data.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("activity.empty")}</p>
      ) : (
        <ol className="flex flex-col divide-y rounded-md border">
          {query.data.data.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
            >
              <span className="font-medium">
                {t(
                  `activity.actor.${entry.actorType === "staff" ? "staff" : entry.actorType === "customer" ? "customer" : "system"}`,
                )}
              </span>
              <span className="flex-1">
                {t(`activity.verb.${auditVerb(entry.action)}`, {
                  what: t(`activity.entity.${auditEntity(entry.entityType)}`),
                })}
              </span>
              <DateJST value={entry.createdAt} className="text-xs text-muted-foreground" />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
